<template>
  <div outer fill style="min-width: 300px">
    <!-- panel head -->
    <div style="flex: none; padding: 14px 16px 12px; display: flex; flex-direction: column; gap: 12px; border-bottom: 1px solid var(--line-inner)">
      <div style="display: flex; align-items: center; gap: 10px">
        <span style="font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.01em">Assets</span>
        <span class="mono" style="font-size: 10.5px; color: var(--text-faint)">{{ assets.length }}{{ capped ? "+" : "" }} indexed</span>
        <div v-if="canEditHere" style="margin-left: auto; display: flex; align-items: center; gap: 6px">
          <button type="button" class="mv-iconbtn" title="New folder" aria-label="New folder" @click="$store.state.createFolder()">
            <v-icon size="16">mdi-folder-plus</v-icon>
          </button>
          <button type="button" class="mv-ghost" @click="$store.state.uploadOpen = true">↑ Upload</button>
        </div>
      </div>

      <!-- toolbar: search with its field, filter, sort, view; wraps on narrow widths -->
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 8px">
        <div class="mv-field" style="flex: 1 1 220px; min-width: 180px">
          <span style="padding: 0 4px 0 11px; color: var(--text-ph); font-size: 14px">⌕</span>
          <input
            style="flex: 1; min-width: 0; padding: 0 8px; font-size: 12.5px"
            placeholder="Search title, filename, TapeID…"
            v-model="$store.state.search.text"
            @input="$store.state.requestAssetsSoon()"
            @keyup.enter="$store.state.requestAssets()" />
          <div class="mv-select-wrap" style="flex: none; border-left: 1px solid var(--line-soft)">
            <select
              title="Search scope"
              style="appearance: none; -webkit-appearance: none; padding: 0 24px 0 10px; color: var(--text-dim); font-size: 12px; font-weight: 600; cursor: pointer"
              v-model="$store.state.search.scope">
              <option v-for="scope in SCOPES" :key="scope.value" :value="scope.value">{{ scope.label }}</option>
            </select>
          </div>
        </div>
        <button type="button" class="mv-ghost" :class="{ 'is-on': filterOpen, 'is-active': criteriaCount }" @click="filterOpen = !filterOpen">
          Filter<span v-if="criteriaCount" class="mv-count-badge">{{ criteriaCount }}</span>
        </button>
        <div class="mv-select-wrap">
          <select
            class="mv-input"
            title="Sort"
            style="width: auto; color: var(--text-btn); font-size: 12px; font-weight: 600; padding-left: 11px"
            :value="$store.state.search.sort"
            @change="onSortChange($event.target.value)">
            <option v-for="(sort, key) in SORTS" :key="key" :value="key">{{ sort.label }}</option>
          </select>
        </div>
        <div class="mv-seg mv-seg--icon">
          <button
            type="button"
            class="mv-seg-btn"
            :class="{ 'is-on': !showListView }"
            :aria-pressed="String(!showListView)"
            title="Grid view"
            aria-label="Grid view"
            @click="showListView = false">
            <v-icon size="16">mdi-view-grid-outline</v-icon>
          </button>
          <button
            type="button"
            class="mv-seg-btn"
            :class="{ 'is-on': showListView }"
            :aria-pressed="String(showListView)"
            title="List view"
            aria-label="List view"
            @click="showListView = true">
            <v-icon size="16">mdi-view-list</v-icon>
          </button>
        </div>
      </div>

      <!-- breadcrumb: where we are, the folders here, paste -->
      <div style="display: flex; align-items: center; gap: 8px; min-height: 28px">
        <FolderBreadcrumb :folders="folders" :show-chips="!searching" />
        <div class="mv-seg" style="margin-left: auto; flex: none; align-self: flex-start">
          <button
            v-for="kind in KINDS"
            :key="kind.value"
            type="button"
            class="mv-seg-btn"
            :class="{ 'is-on': $store.state.search.kind === kind.value }"
            :aria-pressed="String($store.state.search.kind === kind.value)"
            @click="$store.state.search.kind = kind.value">
            {{ kind.label }}
          </button>
        </div>
      </div>

      <MetadataSearch :open="filterOpen" :matches="visibleAssets.length" />
    </div>

    <!-- list / grid -->
    <!-- grid or list -->
    <div style="flex: 1; min-height: 0; overflow: auto" :style="showListView ? {} : { padding: '14px 16px 18px' }">
      <div v-if="!showListView" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(176px, 1fr)); gap: 12px">
        <AssetThumbItem v-for="asset in visibleAssets" :key="asset.id" :asset="asset" />
      </div>
      <template v-else>
        <AssetListItem v-for="asset in visibleAssets" :key="asset.id" :asset="asset" />
      </template>
      <div v-if="!visibleAssets.length" style="padding: 48px 16px; text-align: center; font-size: 13px; color: var(--text-faint)">
        <template v-if="filtered">
          No assets match.
          <button type="button" class="mv-link" style="font-size: 13px" @click="$store.state.clearSearch()">Clear search &amp; filters</button>
        </template>
        <template v-else>No assets here.</template>
      </div>
    </div>

    <div footer style="padding: 0 16px">
      <span>{{ plural(visibleAssets.length, "asset") }}{{ filtered ? " found" : "" }}</span>
      <button v-if="filtered" type="button" class="mv-link" @click="$store.state.clearSearch()">Clear</button>
      <span ellipsis style="margin-left: auto; font-size: 10px; color: var(--text-ph)">{{ SHORTCUT_HINT }}</span>
    </div>
  </div>
</template>

<script>
import { PAGE_LIMIT, SCOPES, KINDS, SORTS, filterAssets, sortAssets } from "@/scripts/assetView.js";
import { isTypingTarget, isOverlayOpen, SHORTCUT_HINT } from "@/scripts/shortcuts.js";
import { plural } from "@/scripts/plural.js";

export default {
  props: {},
  data: () => {
    return {
      showListView: false,
      SCOPES,
      KINDS,
      SORTS,
      SHORTCUT_HINT,
      filterOpen: false,
    };
  },
  computed: {
    /** The loaded level without its folders. */
    assets() {
      return (this.$store.state.clips || []).filter((asset) => !asset.folder);
    },
    /** What the grid and list show: the loaded assets narrowed by scope. */
    visibleAssets() {
      const search = this.$store.state.search;
      const folderId = this.$store.state.getLastFilterParentAsset().id;
      const filtered = filterAssets(this.assets, { scope: search.scope, folderId, searching: this.searching, kind: search.kind });
      return sortAssets(filtered, search.sort, { durationOf: (id) => this.$store.state.durations[id] });
    },
    folders() {
      return (this.$store.state.clips || []).filter((asset) => asset.folder);
    },
    criteriaCount() {
      return this.$store.state.metadataCriteria().length;
    },
    /** Anything narrowing the plain folder listing. */
    filtered() {
      const search = this.$store.state.search;
      return this.searching || search.kind !== "all" || search.scope === "archive";
    },
    searching() {
      return this.$store.state.hasActiveSearch();
    },
    capped() {
      return (this.$store.state.clips || []).length >= PAGE_LIMIT;
    },
    /** Whether the caller may create or paste into the folder currently being browsed. */
    canEditHere() {
      return this.$store.state.canEditIn(this.$store.state.getLastFilterParentAsset());
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onCutPasteKey);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onCutPasteKey);
  },
  methods: {
    plural,
    /** ⌘/Ctrl+X cuts the selected asset, ⌘/Ctrl+V pastes it into the open folder; text fields keep theirs. */
    onCutPasteKey(event) {
      if (!(event.metaKey || event.ctrlKey)) return;
      const state = this.$store.state;
      if (isTypingTarget(event.target) || isOverlayOpen(state)) return;
      const key = event.key.toLowerCase();
      const selected = state.currentClip;
      if (key === "x" && selected.id && !selected.folder && state.canEdit(selected)) {
        state.cutAsset(selected);
      } else if (key === "v" && state.cutAssets.length && this.canEditHere) {
        state.pasteCutAssets();
      } else {
        return;
      }
      event.preventDefault();
    },
    /** Asks the server again only when the sort's field or order changed; a client sort just re-orders. */
    onSortChange(key) {
      const search = this.$store.state.search;
      const previous = SORTS[search.sort];
      search.sort = key;
      // ponytail: Longest probes the whole loaded page, three at a time; a stored duration would spare that
      if (key === "longest") this.$store.state.clips.forEach((asset) => this.$store.state.requestDuration(asset));
      if (SORTS[key].field !== previous.field || SORTS[key].order !== previous.order) this.$store.state.requestAssets();
    },
  },
};
</script>
